import {
  createTipPaymentRequest,
  createTipPaymentSuccess,
  createTipPaymentFailed,
  confirmBlikPaymentRequest,
  confirmBlikPaymentSuccess,
  confirmBlikPaymentFailed,
} from "./reducer";
import { createTipPayment, postTipNotify } from "../../helpers/tip_helper";
import { attachPaymentToPendingTipAction } from "../../helpers/pendingTipAction";

const POLL_INTERVAL = 2000;

// Gość wychodzi do aplikacji banku i wraca - okno musi być na tyle szerokie,
// żeby zmieścić ten spacer razem z rozliczeniem BLIK-a po stronie Stripe.
const PAYMENT_TIMEOUT = 300000; // 5 minut

// Wznowienie po przeładowaniu strony: gość już tu jest, więc czekamy krócej.
const RESUME_TIMEOUT = 120000; // 2 minuty

/**
 * Utwórz płatność napiwku
 * @param {string} eventId - ID eventu
 * @param {number} amount - Kwota w groszach (np. 1000 = 10 PLN)
 * @param {string} guestName - Imię gościa
 * @param {object} pendingAction - Co serwer ma dodać po opłaceniu (opcjonalne)
 */
export const createTip = (eventId, amount, guestName, pendingAction) => async (dispatch) => {
  try {
    dispatch(createTipPaymentRequest());

    const response = await createTipPayment(eventId, {
      amount,
      guestName,
      pendingAction,
    });

    if (response) {
      // Zapamiętane razem z odłożoną akcją: pozwala dokończyć dodanie utworu,
      // gdy telefon zamknie kartę na czas potwierdzania w banku.
      attachPaymentToPendingTipAction(
        response.clientSecret,
        Math.round(amount / 100),
        guestName,
        Boolean(response.pendingActionAccepted),
      );

      dispatch(createTipPaymentSuccess({
        clientSecret: response.clientSecret,
        tipId: response.tipId,
      }));
    }
  } catch (error) {
    const errorMessage = error?.response?.data?.message || error.message || "Failed to create payment";
    dispatch(createTipPaymentFailed(errorMessage));
  }
};

/**
 * Przerwa między odpytaniami Stripe, przerywana powrotem do karty.
 *
 * Gość potwierdza BLIK-a w aplikacji banku, a przeglądarka trzyma wtedy kartę
 * zamrożoną - nic się w niej nie dzieje. Bez tego po powrocie trzeba by czekać na
 * dokończenie uśpionego setTimeout i dopiero kolejne odpytanie zamykało okienko.
 * Tak sprawdzamy płatność od razu, gdy karta znów jest na wierzchu.
 */
const delayOrVisible = (ms) => new Promise((resolve) => {
  if (typeof document === 'undefined') {
    setTimeout(resolve, ms);
    return;
  }

  let settled = false;

  const finish = () => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pageshow', finish);
    resolve();
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') finish();
  };

  const timer = setTimeout(finish, ms);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pageshow', finish);
});

/**
 * Mapowanie błędów Stripe na przyjazne komunikaty
 */
const getErrorMessage = (error) => {
  const errorCode = error?.code;
  const errorType = error?.type;
  const declineCode = error?.decline_code;

  // Błędy BLIK
  if (errorCode === 'payment_intent_authentication_failure') {
    return 'Nie udało się potwierdzić płatności. Sprawdź kod BLIK i spróbuj ponownie.';
  }

  if (errorCode === 'payment_intent_unexpected_state') {
    return 'Płatność jest w nieprawidłowym stanie. Spróbuj ponownie.';
  }

  // Timeout
  if (errorCode === 'payment_intent_payment_attempt_expired') {
    return 'Czas na potwierdzenie płatności minął. Wygeneruj nowy kod BLIK.';
  }

  // Brak środków
  if (declineCode === 'insufficient_funds') {
    return 'Brak wystarczających środków na koncie.';
  }

  // Ogólne błędy decline
  if (declineCode === 'generic_decline' || errorType === 'card_error') {
    return 'Płatność została odrzucona. Sprawdź dane i spróbuj ponownie.';
  }

  // Błąd sieci
  if (errorType === 'api_connection_error') {
    return 'Problem z połączeniem. Sprawdź internet i spróbuj ponownie.';
  }

  // Błąd rate limit
  if (errorType === 'rate_limit_error') {
    return 'Zbyt wiele prób. Poczekaj chwilę i spróbuj ponownie.';
  }

  // Domyślny komunikat
  return error?.message || 'Nie udało się przetworzyć płatności. Spróbuj ponownie.';
};

/**
 * Czeka na rozstrzygnięcie płatności, odpytując Stripe o stan PaymentIntent.
 *
 * Nie polegamy na obietnicy z confirmBlikPayment, bo na telefonie karta idzie w tło
 * na czas potwierdzania przelewu w banku - uśpiony Stripe.js potrafi wtedy nie
 * dokończyć własnego odpytywania i spinner "przetwarzanie" wisi bez końca.
 * Odczyt PaymentIntent po powrocie do karty zawsze mówi prawdę.
 *
 * @param {() => object|null} getConfirmError - błąd z confirmBlikPayment, jeśli już wrócił
 */
const waitForBlikResult = async (stripe, clientSecret, getConfirmError) => {
  const startTime = Date.now();

  // Świeży PaymentIntent ma status requires_payment_method - dopiero potwierdzenie
  // przesuwa go dalej. Bez tej flagi pierwszy odczyt wyglądałby jak zły kod BLIK.
  let confirmationStarted = false;

  while (Date.now() - startTime < PAYMENT_TIMEOUT) {
    const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
    const confirmError = getConfirmError();

    if (paymentIntent) {
      switch (paymentIntent.status) {
        case 'succeeded':
        case 'processing':
          // processing = bank potwierdził, Stripe jeszcze księguje. Nie ma na co czekać:
          // napiwek i opłacony nim utwór zapisuje serwer (webhook albo /tips/notify),
          // więc kończymy tu, zamiast trzymać gościa przy spinnerze.
          return paymentIntent;

        case 'canceled':
          throw new Error('PAYMENT_CANCELED');

        case 'requires_action':
        case 'requires_confirmation':
          // Kod poszedł do banku i czekamy, aż gość potwierdzi przelew.
          confirmationStarted = true;
          break;

        case 'requires_payment_method':
          // Cofnięcie do tego stanu po rozpoczęciu potwierdzania oznacza odrzucenie
          // albo wygaśnięcie kodu.
          if (confirmationStarted || confirmError) {
            throw confirmError || new Error('INVALID_BLIK_CODE');
          }
          break;

        default:
          break;
      }
    }

    // Stripe.js zdążył zwrócić twardy błąd (np. nieprawidłowy kod) - nie ma na co czekać.
    if (confirmError && !confirmationStarted) {
      throw confirmError;
    }

    await delayOrVisible(POLL_INTERVAL);
  }

  // Ostatnie słowo należy do stanu płatności, nie do zegara.
  const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
  if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
    return paymentIntent;
  }

  throw new Error('REQUIRES_ACTION_TIMEOUT');
};

/**
 * Potwierdzenie płatności BLIK
 */
export const confirmBlikPayment = (
  stripe,
  clientSecret,
  blikCode,
  amount,
  guestName,
  eventId
) => async (dispatch) => {
  dispatch(confirmBlikPaymentRequest());

  let confirmError = null;

  // Celowo bez await: obietnica bywa rozwiązywana dopiero po powrocie do karty,
  // a o stanie płatności decyduje odpytywanie w waitForBlikResult.
  stripe
    .confirmBlikPayment(clientSecret, {
      payment_method: {
        blik: {},
      },
      payment_method_options: {
        blik: {
          code: blikCode,
        },
      },
    })
    .then(({ error }) => {
      if (error) {
        confirmError = error;
      }
    })
    .catch((error) => {
      confirmError = error;
    });

  try {
    const paymentIntent = await waitForBlikResult(stripe, clientSecret, () => confirmError);

    // Powiadom hosta i zapisz napiwek (webhook Stripe zrobi to samo - zapis jest idempotentny).
    try {
      await postTipNotify(eventId, {
        amount: amount,
        guestName: guestName,
        paymentIntentId: paymentIntent.id,
      });
    } catch (e) {
      console.warn('Notify host about tip failed (but payment ok):', e);
    }

    dispatch(confirmBlikPaymentSuccess());
    return true;
  } catch (error) {
    if (error.message === 'REQUIRES_ACTION_TIMEOUT') {
      dispatch(confirmBlikPaymentFailed(
        'Nie udało się potwierdzić płatności w aplikacji bankowej. Spróbuj ponownie z nowym kodem BLIK.'
      ));
      return false;
    }

    if (error.message === 'PAYMENT_CANCELED') {
      dispatch(confirmBlikPaymentFailed('Płatność została anulowana.'));
      return false;
    }

    if (error.message === 'INVALID_BLIK_CODE') {
      dispatch(confirmBlikPaymentFailed(
        'Nieprawidłowy kod BLIK. Wygeneruj nowy kod i spróbuj ponownie.'
      ));
      return false;
    }

    dispatch(confirmBlikPaymentFailed(getErrorMessage(error)));
    return false;
  }
};

/**
 * Sprawdza płatność odłożonej akcji po przeładowaniu strony.
 *
 * Telefon potrafi zamknąć kartę, gdy gość potwierdza BLIK-a w banku - wracamy wtedy
 * do świeżo wczytanej strony, na której nikt już nie czeka na wynik płatności.
 * Jedynym śladem jest clientSecret zapisany w localStorage, więc stan płatności
 * czytamy prosto ze Stripe i dopisujemy napiwek (API zapisuje go idempotentnie,
 * więc powtórka z webhookiem niczego nie zdubluje).
 *
 * Gdy płatność wciąż czeka na gościa (kod poszedł do banku, ale nie ma jeszcze
 * potwierdzenia), odpytujemy przez chwilę, a potem zwracamy 'pending' - odłożona
 * akcja ma wtedy zostać w localStorage do następnego wejścia na stronę.
 *
 * @returns {Promise<'paid'|'failed'|'pending'>}
 */
export const verifyPendingTipPayment = async (stripe, pending, timeout = RESUME_TIMEOUT) => {
  if (!stripe || !pending?.clientSecret) {
    return 'failed';
  }

  const startTime = Date.now();
  let paymentIntent = null;

  do {
    try {
      ({ paymentIntent } = await stripe.retrievePaymentIntent(pending.clientSecret));
    } catch (e) {
      console.warn('Cannot verify pending tip payment:', e);
      return 'pending';
    }

    const status = paymentIntent?.status;

    if (status === 'succeeded' || status === 'processing') {
      break;
    }

    // requires_payment_method po przeładowaniu strony znaczy tyle, że kod BLIK
    // przepadł razem z kartą - nie ma czego wznawiać.
    if (status !== 'requires_action' && status !== 'requires_confirmation') {
      return 'failed';
    }

    await delayOrVisible(POLL_INTERVAL);
  } while (Date.now() - startTime < timeout);

  if (paymentIntent?.status !== 'succeeded' && paymentIntent?.status !== 'processing') {
    return 'pending';
  }

  if (pending.amount) {
    try {
      await postTipNotify(pending.eventId, {
        amount: pending.amount,
        guestName: pending.guestName,
        paymentIntentId: paymentIntent.id,
      });
    } catch (e) {
      console.warn('Notify host about tip failed (but payment ok):', e);
    }
  }

  return 'paid';
};

export { resetTipState } from "./reducer";
